import axios from "axios";
import CacheStorageService from "./cacheStorage.service";

class UserService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl;
  }

  /**
   * @author Leonel Contreras
   * @description Handle event to get the user information from auth0
   * @date 20-07-2020
   * @param {Object} authOId
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async getUser(authOId, token) {
    let response = await axios.get(`${this.apiUrl}/user?auth0Id=${authOId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data;
  }

  /**
   * @author Lucia Alvarado
   * @description Get all signers from an existing token user-client in A3Cloud and hibou.legal
   * @date 30-09-2020
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async getUserSigners(token) {
    let response = await axios.get(`${this.apiUrl}/user-signers`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data;
  }

  /**
   * @author Lucia Alvarado
   * @description  Get by email a signer from an existing user in Auth0, A3Cloud and hibou.legal
   * @date 30-09-2020
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async getUserByEmail(email, token) {
    email = email.toLowerCase();
    let response = await axios.get(`${this.apiUrl}/user-signer`, email, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }

  /**
   * @author Lucia Alvarado
   * @description Create a new collaborator signer from an existing user in Auth0, A3Cloud and hibou.legal
   * @date 30-09-2020
   * @param {Number} roleId
   * @param {String} userEmail
   * @param {String} token
   * @param {Number} roleContract
   * @param {Number} roleDocs
   * @param {Object} user
   * @returns {Object}
   * @memberof UserService
   */
  async createCollaborator(roleId, userEmail, token, roleContract, roleDocs) {
    userEmail = userEmail.toLowerCase();

    let response = await axios.post(
      `${this.apiUrl}/user-signer`,
      { roleId, userEmail, roleDocs },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }

  /**
   * @author Lucia Alvarado
   * @description Update a signer from an existing user in Auth0, A3Cloud and hibou.legal
   * @date 30-09-2020
   * @param {Number} roleId
   * @param {String} email
   * @param {String} token
   * @param {Number} roleContract
   * @param {Number} roleDocs
   * @param {Object} user
   * @returns {Object}
   * @memberof UserService
   */
  async updateCollaborator(roleId, email, token, roleContract, roleDocs) {
    email = email.toLowerCase();

    let response = await axios.put(
      `${this.apiUrl}/user-signer`,
      { roleId, email, roleContract, roleDocs },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Lucia Alvarado
   * @description Update the basic user information from hibou.legal in A3Cloud
   * @updatedDate 06-03-2022
   * @param {String} token
   * @param {String} profileData
   * @returns {Object}
   * @memberof UserService
   */
  async updatedUserProfileAddress(profileData, token) {
    let response = await axios.put(
      `${this.apiUrl}/user-profile-address`,
      profileData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Alan Aguilar
   * @description Update the basic user information from hibou.legal in A3Cloud
   * @updatedDate 06-03-2022
   * @param {String} userProfileData
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async updateUserProfileInfo(userProfileData, token) {
    let response = await axios.put(
      `${this.apiUrl}/user-profile-name`,
      userProfileData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Lucia Alvarado
   * @description Handle event to create a new user in A3cloud and hibou.legal
   * @date 20-07-2020
   * @param {String} token
   * @param {Object} user
   * @returns {Object}
   * @memberof UserService
   */
  async createUser(token, user) {
    let {
      firstName,
      clientId,
      addressId,
      firstLastName,
      secondLastName,
      phoneNumber,
      cellphoneNumber,
      userTypeId,
      languageId,
      rfc,
    } = user;

    let response = await axios.post(
      `${this.apiUrl}/user`,
      {
        firstName,
        clientId,
        addressId,
        firstLastName,
        secondLastName,
        phoneNumber,
        cellphoneNumber,
        userTypeId,
        languageId,
        rfc,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }

  /**
   * @author Alan Aguilar
   * @description Handle event to update the user information in A3cloud and hibou.legal
   * @date 05-10-2020
   * @param {Object} user
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async updateUser(user, token) {
    let response = await axios.put(`${this.apiUrl}/user`, user, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * @author Lucia Alvarado
   * @description Delete a signer from an existing user in Auth0, A3Cloud and hibou.legal
   * @date 20-07-2020
   * @param {String} token
   * @param {Object} user
   * @returns {Object}
   * @memberof UserService
   */

  async deleteCollaborator(email, token) {
    email = email.toLowerCase();

    let response = await axios.delete(
      `${this.apiUrl}/user-signer?email=${email}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Martín Sánchez
   * @description Get user email
   * @date 29-09-2020
   * @param {String} token
   * @returns {Object}
   * @memberof ContractService
   */
  async getUserEmail(email, token) {
    email = email.toLowerCase();

    let resp = await axios
      .get(`${this.apiUrl}/user-email?email=${email}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(function (response) {
        return response.data.data;
      })
      .catch(function (error) {
        return { response: "error,", data: error };
      });
    return resp;
  }

  /**
   * @author Martín Sánchez
   * @description Get user names
   * @date 29-09-2020
   * @param {String} token
   * @returns {Object}
   * @memberof ContractService
   */
  async getUserName(email, token) {
    email = email.toLowerCase();

    let resp = await axios
      .get(`${this.apiUrl}/user-name?email=${email}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(function (response) {
        return response.data.data;
      })
      .catch(function (error) {
        return { response: "error,", data: error };
      });
    return resp;
  }

  /**
   * @author Martín Sánchez
   * @description Get colaboradores
   * @date 25-11-2020
   * @param {String} token
   * @returns {Object}
   * @memberof ContractService
   */
  async getCollaborator(email, token) {
    email = email.toLowerCase();

    let resp = await axios
      .get(`${this.apiUrl}/collaborator-email?email=${email}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(function (response) {
        return response.data.data;
      })
      .catch(function (error) {
        return { response: "error,", data: error };
      });
    return resp;
  }

  /**
   * @author Martín Sánchez
   * @description Get user invitado
   * @date 25-11-2020
   * @param {String} token
   * @returns {Object}
   * @memberof ContractService
   */
  async getInvitados(email, token) {
    email = email.toLowerCase();

    let resp = await axios
      .get(`${this.apiUrl}/invited-email?email=${email}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(function (response) {
        return response.data.data;
      })
      .catch(function (error) {
        return { response: "error,", data: error };
      });
    return resp;
  }

  /**
   * @author Alan Aguilar
   * @description Get collaborators and invites
   * @updatedDate 05-10-2021
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async getUsersCollaboratorInvite(token) {
    const url = new URL(`${this.apiUrl}/users-collaborator-invite`);
    
    const cache = new CacheStorageService();
    const response = await cache.fetchData(url, token);
    return response.data.data;
  }

  /**
   * @author Lucia Alvarado
   * @description Handle event to create the request reliable
   * @date 21-07-2021
   * @param {String} token
   * @param {Object} signRequestDocumentData
   * @returns {Object}
   * @memberof UserService
   */
  async createKeysCertificate(signRequestDocumentData, token) {
    let response = await axios.post(
      `${this.apiUrl}/user-generate-keys-certificate`,
      signRequestDocumentData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Edgar Escobedo
   * @description Crear usuario colaborador en Contracts o en Docs
   * @date 06-06-2022
   * @param {String} roleContractId
   * @param {String} flagContractId
   * @param {String} roleDocsId
   * @param {string} flagDocsId
   * @returns {Object}
   * @memberof UserService
   */
  async createCollaboratorInGrcContractOrdocs(
    firstName,
    lastname,
    secondlastname,
    email,
    roleContract,
    createUserinGrcContract,
    roleDocs,
    createUserinGrcDocs,
    emailAdmin,
    token
  ) {
    email = email.toLowerCase();

    let response = await axios.post(
      `${this.apiUrl}/create-user-colab-contracts-docs`,
      {
        firstName,
        lastname,
        secondlastname,
        email,
        roleDocs,
        createUserinGrcDocs,
        emailAdmin,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }

  /**
   * @author Edgar Escobedo
   * @description get User from Contracts
   * @date 06-06-2022
   * @param {String} email
   * @returns {Object}
   * @memberof UserService
   */

  async getUserContractByEmail(email, flagContracs, flagDocs, token) {
    email = email.toLowerCase();

    let response = await axios.post(
      `${this.apiUrl}/get-user-contracts-docs`,
      { email, flagContracs, flagDocs },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Get collaborators
   * @updatedDate 23-06-2022
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async getUsersCollaboratorAdmin(token) {
    let response = await axios.get(`${this.apiUrl}/users-collaborator-admin`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Get roles signature profile for users
   * @updatedDate 8-7-2022
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */

  async getRolesSignatureProfile(token) {
    let response = await axios.get(`${this.apiUrl}/roles-signatures-profiles`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Get roles for user by email
   * @updatedDate 11-7-2022
   * @param {String} token
   * @returns {Object}
   * @memberof UserService
   */
  async getRolesForUserByEmail(token, email) {
    email = email.toLowerCase();

    let response = await axios.get(
      `${this.apiUrl}/roles-user-by-email?email=${email}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @updatedBy Mario Carrizales 23-06-2023
   * @description Creates a new user collaborator
   * @updatedDate 14-7-2022
   * @param {String} token
   * @param {userData} body
   * @returns {Object}
   * @memberof UserService
   */
  async createNewUserCollaborator(token, userData) {
    userData.email = userData.email.toLowerCase();

    let response = await axios.post(
      `${this.apiUrl}/create-new-user-collaborator`,
      userData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Creates roles and signature profiles for user
   * @updatedDate 14-7-2022
   * @param {String} token
   * @param {userProfileData} body
   * @returns {Object}
   * @memberof UserService
   */

  async createRolesAndSignatureProfilesForUser(token, userProfileData) {
    let response = await axios.post(
      `${this.apiUrl}/roles-signatures-profiles`,
      userProfileData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Update user status
   * @updatedDate 14-7-2022
   * @param {String} token
   * @param {userProfileData} body
   * @returns {Object}
   * @memberof UserService
   */

  async updateUserStatus(token, userStatus) {
    let response = await axios.put(
      `${this.apiUrl}/update-user-status`,
      userStatus,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Get current roles companies
   * @updatedDate 18-7-2022
   * @param {String} token
   * @param {userEmail} body
   * @returns {Object}
   * @memberof UserService
   */
  async getUserRolesCompany(token, userEmail) {
    userEmail = userEmail.toLowerCase();
    let response = await axios.get(
      `${this.apiUrl}/user-roles-company?email=${userEmail}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Save roles for user company
   * @updatedDate 18-7-2022
   * @param {String} token
   * @param {userComapanies} body
   * @returns {Object}
   * @memberof UserService
   */

  async saveRolesForUserByCompany(token, userComapanies) {
    let response = await axios.post(
      `${this.apiUrl}/company-roles-for-user`,
      userComapanies,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Edgar Escobedo
   * @description get password user from Hibou
   * @date 28-06-2022
   * @param {String} email
   * @returns {Object}
   * @memberof UserService
   */
  async getPasswordUserFromHibou(email, token) {
    email = email.toLowerCase();
    let response = await axios.get(
      `${this.apiUrl}/get-password-user?email=${email}&token=${token}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Get questionnaries for user
   * @param {String} token
   * @returns {Array}
   */
  async getQuestionnairesByUserId(token, userId) {
    let response = await axios.get(
      `${this.apiUrl}/questionnaires?clientId=${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }

  /**
   * @author Pedro Antonio
   * @description Get all users that can be shared documents
   * @param {String} token
   * @returns {Object}
   */
  async getUsersToShareDocuments(token) {
    let response = await axios.get(
      `${this.apiUrl}/users-to-share-documents`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @typedef {Object} UserResponse
   * @prop {string} consecutive
   * @prop {number} statusCode
   * @prop {string} message
   * @prop {object | null} data
   */

  /**
   * @author Pedro Antonio
   * @param {string} email
   * @static
   * @async
   * @returns {Promise<UserResponse>} userData
   * @memberof UserService
   */
  static async getUserPublic(email) {
    const url = new URL(`${process.env.REACT_APP_MIFIRMA_API}/user-public`)
    if (email) url.searchParams.append("email", email)
    return axios.get(url)
      .then(response => response.data)
  }
  /**
   * @author Pedro Antonio
   * @param {string} email
   * @static
   * @async
   * @returns {Promise<UserResponse>} userData
   * @memberof UserService
   */
  static async updateUserPublic(userData) {
    const url = new URL(`${process.env.REACT_APP_MIFIRMA_API}/user-public`)
    return axios.put(url, { ...userData })
      .then(response => response.data)
  }

  /**
  * @author Edgar Escobedo
  * @description Delete user in Auth0 and disable in A3
  * @date 19-09-2024
  * @param {String} email
  * @param {String} token
  * @returns {Object}
  */
  async deleteUserInAuth0(email, token){
    email = email.toLowerCase();

    let response = await axios.put(
      `${this.apiUrl}/user-auth0-by-email`,
      { email },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

    /**
   * @author Edgar Escobedo
   * @description Get collaborators or invites wiht limit and offset
   * @updatedDate 25-10-2024
   * @param {String} token
   * @param {Boolean} collaborators
   * @param {Boolean} invites
   * @param {Number} offset
   * @param {Number} limit
   * @returns {Object}
   * @memberof UserService
   */
    async getUsersCollaboratorsOrInvites(token, collaborators, invites, offset, limit, emailSearch) {
      const url = new URL(`${this.apiUrl}/users-collaborators-invites`);
      if(collaborators) url.searchParams.append('collaborators', true);
      if(invites) url.searchParams.append('invites', true);
      if(offset !== undefined && offset !== null) url.searchParams.append('offset', offset);
      if(limit) url.searchParams.append('limit', limit);
      if(emailSearch) url.searchParams.append('emailSearch', emailSearch);
      
      const cache = new CacheStorageService();
      const response = await cache.fetchData(url, token);
      return response.data.data;
    }
  
  /**
  * @author Edgar Escobedo
  * @description Update language to user
  * @createdDate 29-10-2024
  * @param {String} isoKey
  * @param {String} token
  * @returns {Object}
  */
  async updateLangageUser(isoKey, token){
    let response = await axios.post(
      `${this.apiUrl}/user-language`,
      { isoKey },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }
}

export { UserService as default };
